import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Alert, AlertDescription } from '../ui/alert';
import { Spinner } from '../ui/spinner';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Lock, ChevronDown } from 'lucide-react';
import { vipAPI } from '../../services/api';
import { useBrand } from '../../context/BrandContext';

function buildSystemPrompt(brandName) {
  const name = brandName || 'BitStarz';
  return `You are an AI co-pilot for a ${name} VIP manager. Your job: turn raw player data into 3 concrete, executable decisions the manager can act on this week.

The primary lever is bonuses. Email is the outreach channel. Calls are not part of this workflow.

Each card must name exactly one DECISION from this list and explain why now:
- GIVE BONUS — recommend a specific type (deposit/freespin/cashback), exact timing, and expected impact
- CUT BONUS — reduce or pause bonus spend, with clear reason (bonus-dependent, negative ROI, abuse risk)
- EMAIL — send a personalised email; draft the subject line and body word-for-word
- HOLD — no action needed this week; state what to watch and when to revisit

Recency rule: the last 7 days are the primary signal. Days 8–30 are baseline and context only. If the last 7 days show a clear reversal (recovery after a gap, sudden drop, spend spike), that overrides the 30-day average. Always state whether the 7-day trend confirms or contradicts the 28-day trend.

Bonus decisions require bonus ROI reasoning: only recommend GIVE BONUS if ggr_rol7d or recent GGR shows the player generates real returns. If GGR trend is flat or negative, a bonus cost will not be recovered — recommend HOLD or CUT BONUS instead. Also assess: are bonuses lifting real play or just subsidising bonus wagering? Use the bonus history section to determine which type has historically converted for this player.

Email decisions require timing reasoning: what specific signal says "contact now" vs "wait"? The ideal moment is when a player has just returned from a gap — they are re-engaging and receptive. Cooling-off patterns warrant an email before the player goes fully silent.

No-signal rule: if the data shows stable activity with no meaningful trend change or bonus ROI opportunity, all 3 cards MAY be HOLD. State exactly which metric to watch and when to revisit. Do not manufacture urgency — a correct HOLD is more useful than a forced action.

GGR > 0 = casino wins, player loses. NGR = GGR + bonus_canceled - bonus_issued (the dashboard's ngr3 definition, used everywhere in this app). NGR > 0 = player lost overall (net of bonuses); NGR < 0 = player won overall (net of bonuses). "Monitor" is never a decision — every card needs an action the manager can execute today.

Do not mention welfare, responsible gambling, or escalation. This is a commercial VIP retention tool only — every decision is GIVE BONUS, CUT BONUS, EMAIL, or HOLD.

Return exactly 3 cards. No text before the first CARD_START or after the last CARD_END. No markdown outside the card blocks.

CARD_START
URGENCY: [high/medium/low]
TRIGGER: [max 4 words, UPPERCASE — the signal that created this card]
INSIGHT: [2-3 sentences: what is happening and what it means for this player's VIP trajectory]
EVIDENCE:
- [specific metric with date/value]
- [specific metric with date/value]
ACTION: [DECISION keyword] — [exact action the manager takes today]
SAY: [ready-to-send email subject + body — copy-paste ready]
AVOID: [what NOT to do or say with this specific player right now]
FOLLOWUP: [next step if no response in 72h]
CARD_END`;
}

const DEFAULT_PROMPT = `Analyse this player through a VIP manager's lens. Each of the 3 cards must answer ONE question with real data and name a specific executable decision.

RECENCY
- Weight the last 7 days heavily. Compare ggr_rol7d vs ggr_eur — is the recent trend above or below the personal baseline?
- A reversal in the last 7 days (recovery, sudden drop, deposit spike) overrides the 30-day picture. State this explicitly.

BONUS STRATEGY
- ROI gate: only recommend GIVE BONUS if ggr_rol7d is positive and trending. If GGR is flat or negative, a bonus will not be recovered — recommend HOLD or CUT BONUS.
- Type fit: use the bonus history section to find which bonus type drove the best post-bonus GGR for this player. Freespin vs deposit bonus vs cashback — which has converted?
- Decision: GIVE BONUS (type, timing, expected ROI), CUT BONUS (reason), or HOLD if ROI is unclear.

CHURN & EMAIL TIMING
- Drift signals: is bet_count dropping toward 0? Is ggr_rol7d falling? Is PoBA declining?
- Best moment to email: player just returned after a gap (bet_count > 0 after ≥5 inactive days) — they are re-engaging and receptive.
- Pre-churn moment: PoBA falling for 2+ consecutive weeks — email before they go fully silent.
- Decision: EMAIL (with a specific reason) or HOLD. No calls.

SPEND TRAJECTORY
- Is stake accelerating or decelerating? Compare stake_rol7d vs prior weeks and use the 180-day trajectory slopes.
- VIP value: is this player growing, stable, or at risk based on current trajectory?

Rules:
- Cite actual column values and dates. Never say "appears to" — use the number.
- Do not invent data. Only use what is provided.
- Do not mention welfare, responsible gambling, or escalation. This is a commercial VIP retention tool only — every decision is bonus, email, or hold.`;

const DS_OPTIONS = [
  { key: 'heatmap',    label: 'Time of play' },
  { key: 'churn',      label: 'Churn prediction' },
  { key: 'bonuses',    label: 'Bonus history' },
  { key: 'trajectory', label: 'Trend slopes' },
];

const DS_DEFAULTS = {
  heatmap: `--- TIME OF PLAY GUIDANCE ---
Play pattern data is included. Use it to:
- Find the player's peak active hours and days (highest stake slots)
- Every bonus or email recommendation MUST include a specific send time tied to this data (e.g. "send Tuesday 20:00 UTC — their highest-stake slot")
- If the schedule is sparse (fewer than 10 active time slots), timing precision matters even more — contact outside active windows will be ignored`,

  churn: `--- CHURN / PoBA GUIDANCE ---
PoBA (Probability of Being Alive) trend data is included. Use it to:
- PoBA near 1 = active; near 0 = churned. A falling trend = disengaging, rising = returning
- Falling PoBA: EMAIL urgency is higher and the bonus ROI window is closing — act before the trend bottoms out
- PoBA dropping sharply below 0.3 = high churn risk — mark urgency HIGH and recommend EMAIL (win-back before full churn) or CUT BONUS (stop spending on a likely-churned player)
- Rising PoBA: player is re-engaging — ideal moment for a well-timed bonus while momentum is positive
- If PoBA recovered after a specific bonus type, flag it: "PoBA rose +0.X after [bonus type] on [date] — repeat this"`,

  bonuses: `--- BONUS HISTORY GUIDANCE ---
Individual bonus records are included. Use them to:
- Identify the largest bonuses and check GGR in the 3–7 days after — did they generate a real return?
- Flag any unusually large wins (jackpot, high multiplier) in the period — these temporarily inflate activity and distort the baseline; do not treat post-jackpot behaviour as a sustainable trend
- Assess which bonus type (deposit, freespin, weekend) drove the highest post-bonus GGR for this player — recommend that type specifically
- Low activation rate = player is not engaging with the current bonus type; switch type or cut spend`,

  trajectory: `--- TREND SLOPE GUIDANCE ---
180-day trend slopes are included. Use them to:
- Positive stake slope + positive GGR slope = growing player, strong bonus ROI candidate
- Negative GGR slope + rising deposit slope = player depositing more but winning less — weak bonus ROI candidate, favour HOLD or CUT BONUS over a new offer
- Flat or declining bet count slope = disengaging — email timing is critical, contact before full churn sets in
- Recent vs prior ratio > 1.2 = clear acceleration; < 0.8 = clear deceleration — reflect this in card urgency`,
};

const SECTIONS = [{ key: 'insights', label: 'Insights' }];

function PromptEditor({ label, value, onChange, systemPrompt }) {
  return (
    <div>
      <div className="flex justify-between items-center mb-1">
        <span className="text-[13px] text-ink">{label}</span>
        <button onClick={() => onChange(DEFAULT_PROMPT)} className="text-[11px] text-ink-dim border border-white/10 rounded px-2 py-0.5 hover:border-brand-yellow hover:text-brand-yellow hover:bg-brand-yellow/10 transition-colors cursor-pointer">Reset to default</button>
      </div>
      <div className="relative border border-white/8 rounded-t overflow-hidden bg-surface">
        <div className="flex items-center gap-1 px-2 py-1 border-b border-white/8 bg-surface">
          <Lock className="h-3 w-3 text-ink-dim" /><span className="text-[10px] text-ink-dim uppercase tracking-wider">System prompt — locked</span>
        </div>
        <pre className="m-0 px-2 py-2 text-[11px] text-ink-mute font-mono whitespace-pre-wrap leading-relaxed select-none pointer-events-none">{systemPrompt}</pre>
      </div>
      <div className="border-x border-white/8 bg-[#0d0f12] px-2 py-1 flex items-center gap-2">
        <div className="flex-1 h-px bg-white/8" /><span className="text-[10px] text-brand-yellow uppercase tracking-wider shrink-0">Your instructions — editable</span><div className="flex-1 h-px bg-white/8" />
      </div>
      <Textarea minRows={8} value={value} onChange={e => onChange(e.target.value)} placeholder="Enter your analysis instructions..." className="font-mono text-xs rounded-t-none border-t-0 [&_textarea]:rounded-t-none [&]:border-t-0 focus:border-t-0" />
    </div>
  );
}

function DatasourcePromptEditor({ dsKey, label, value, onChange }) {
  const [open, setOpen] = useState(false);
  const current = value ?? DS_DEFAULTS[dsKey];
  const isDefault = !value || value === DS_DEFAULTS[dsKey];

  return (
    <div className="border border-white/8 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 bg-surface hover:bg-surface-2 transition-colors text-left"
      >
        <div className="flex items-center gap-2.5">
          <span className="text-[13px] font-medium text-ink">{label}</span>
          {!isDefault && (
            <span className="text-[10px] uppercase tracking-wider text-brand-yellow border border-brand-yellow/40 rounded px-1.5 py-0.5">
              modified
            </span>
          )}
        </div>
        <ChevronDown className={`h-4 w-4 text-ink-dim transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="border-t border-white/8 p-4 flex flex-col gap-2 bg-[#0d0f12]">
          <Textarea
            minRows={6}
            value={current}
            onChange={e => onChange(e.target.value)}
            className="font-mono text-xs"
          />
          <div className="flex justify-end">
            <button
              onClick={() => onChange(DS_DEFAULTS[dsKey])}
              disabled={isDefault}
              className="text-[11px] text-ink-dim border border-white/10 rounded px-2 py-0.5 hover:border-brand-yellow hover:text-brand-yellow hover:bg-brand-yellow/10 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-default"
            >
              Reset to default
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function InsightsSettings() {
  const { activeBrandInfo } = useBrand() || {};
  const brandDisplayName = activeBrandInfo?.display_name || 'BitStarz';
  const systemPrompt = buildSystemPrompt(brandDisplayName);

  const empty = { active_provider: 'anthropic', anthropic_prompt: '', openai_prompt: '', datasource_prompts: {} };
  const [saved, setSaved] = useState(empty);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await vipAPI.getSettings();
        const data = res.data?.settings;
        if (data) {
          const state = {
            active_provider: data.active_provider || 'anthropic',
            anthropic_prompt: data.anthropic_prompt || '',
            openai_prompt: data.openai_prompt || '',
            datasource_prompts: data.datasource_prompts || {},
          };
          setSaved(state);
          setForm(state);
        }
      }
      catch (err) { if (err.response?.status !== 404) setErrorMsg('Failed to load settings.'); }
      finally { setLoading(false); }
    })();
  }, []);

  const isDirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(saved), [form, saved]);
  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));
  const setDS = (dsKey, val) => setForm(prev => ({
    ...prev,
    datasource_prompts: { ...prev.datasource_prompts, [dsKey]: val },
  }));

  const handleSave = async () => {
    setSaving(true); setSuccessMsg(''); setErrorMsg('');
    try { await vipAPI.saveSettings(form); setSaved(form); setSuccessMsg('Settings saved.'); } catch { setErrorMsg('Failed to save settings.'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center pt-8"><Spinner size="sm" /></div>;

  const SaveBtn = () => (
    <Button onClick={handleSave} disabled={saving || !isDirty} variant={isDirty ? 'default' : 'outline'} size="sm" className="gap-1.5">
      {saving && <Spinner size="sm" />}{saving ? 'Saving...' : 'Save'}
    </Button>
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-between items-start">
        <div><h3 className="text-[15px] font-bold text-ink mb-0.5">Insights</h3><p className="text-xs text-ink-dim">Each provider has its own prompt. Select the active provider to choose which one is used when generating insights.</p></div>
        <SaveBtn />
      </div>
      {successMsg && <Alert variant="success" className="text-xs"><AlertDescription>{successMsg}</AlertDescription></Alert>}
      {errorMsg && <Alert variant="error" className="text-xs"><AlertDescription>{errorMsg}</AlertDescription></Alert>}
      <div className="bg-surface-1 border border-white/10 rounded-lg p-5 flex flex-col gap-5">
        <div className="max-w-[280px]">
          <label className="text-xs text-ink-dim block mb-1">Active Provider</label>
          <Select value={form.active_provider} onValueChange={v => set('active_provider', v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="anthropic">Anthropic (Claude)</SelectItem>
              <SelectItem value="openai">OpenAI (GPT-4o)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <PromptEditor label="Anthropic Prompt" value={form.anthropic_prompt} onChange={val => set('anthropic_prompt', val)} systemPrompt={systemPrompt} />
        <PromptEditor label="OpenAI Prompt" value={form.openai_prompt} onChange={val => set('openai_prompt', val)} systemPrompt={systemPrompt} />
        <p className="text-[11px] text-ink-dim">Player data (summary + 30-day vip_dashboard_daily + heatmap + churn + bonuses + trajectory) is automatically included in every analysis.</p>
        <div className="flex justify-end"><SaveBtn /></div>
      </div>
      <div className="bg-surface-1 border border-white/10 rounded-lg p-5 flex flex-col gap-5">
        <div>
          <h4 className="text-[13px] font-bold text-ink mb-0.5">Datasource instructions</h4>
          <p className="text-xs text-ink-dim">All 4 datasources are always included in every analysis. Each instruction is injected into the system prompt automatically. Blank = use built-in default.</p>
        </div>
        <div className="flex flex-col gap-2">
          {DS_OPTIONS.map(({ key, label }) => (
            <DatasourcePromptEditor
              key={key}
              dsKey={key}
              label={label}
              value={form.datasource_prompts[key]}
              onChange={val => setDS(key, val)}
            />
          ))}
        </div>
        <div className="flex justify-end"><SaveBtn /></div>
      </div>
    </div>
  );
}

const SECTION_CONTENT = { insights: <InsightsSettings /> };

export default function SettingsView() {
  const [activeSection, setActiveSection] = useState('insights');
  return (
    <div className="flex gap-0 min-h-[calc(100vh-120px)]">
      <div className="w-[180px] shrink-0 border-r border-white/8 pr-3 pt-2">
        <span className="text-[10px] text-ink-dim uppercase tracking-widest block mb-2 px-2">Settings</span>
        {SECTIONS.map(s => (
          <div key={s.key} onClick={() => setActiveSection(s.key)} className={`px-2 py-1.5 rounded cursor-pointer text-[13px] transition-colors ${activeSection === s.key ? 'bg-surface-2 text-ink font-bold' : 'text-ink-dim hover:bg-surface-2 hover:text-ink'}`}>            {s.label}
          </div>
        ))}
      </div>
      <div className="flex-1 pl-6 pt-2 max-w-[760px]">{SECTION_CONTENT[activeSection]}</div>
    </div>
  );
}
